/**
 * "Remember me" storage for the login screens.
 *
 * Stores the email *and* password, encrypted with AES-GCM via Web Crypto, so
 * the values are not legible in the devtools storage viewer.
 *
 * ---------------------------------------------------------------------------
 * READ THIS BEFORE RELYING ON THE ENCRYPTION
 *
 * This is obfuscation, not protection, and it is deliberate.
 *
 * The login page has to decrypt the password to put it back in the form, so
 * the key has to be derivable inside the browser — which means it ships in the
 * JS bundle. Anyone who opens devtools can import this module and call
 * `readRememberedCredentials()` to get the plaintext back, and any XSS on any
 * page of the app can do the same silently. Encrypting raises the effort from
 * "read it off the screen" to "open the console"; it does not make the
 * password secret from an attacker who reaches the browser.
 *
 * What it does buy: the value is no longer exposed to a shoulder-surfer, a
 * screen-share, a screenshot of devtools, or a support session where someone
 * is poking around in Application → Local Storage.
 *
 * If the requirement ever becomes real confidentiality, the answer is not a
 * stronger cipher here — it is to stop storing the password at all and use
 * either the browser's own credential manager (the login inputs already carry
 * `autocomplete="current-password"`) or a long-lived refresh token so the user
 * skips the login form entirely.
 * ---------------------------------------------------------------------------
 *
 * Storage formats handled, newest first:
 *   `v2:<base64>`                        AES-GCM payload (current)
 *   `user@example.com`                   email-only, no password
 *   `{"email":"…","password":"…"}`       legacy PLAINTEXT, migrated on sight
 */

export const CRM_REMEMBER_ME_KEY = 'crm_remember_me';
export const PORTAL_REMEMBER_ME_KEY = 'portal_remember_me';

export interface RememberedCredentials {
  email: string;
  password: string;
}

const ENCRYPTED_PREFIX = 'v2:';

/**
 * Key material. Bundled, and therefore public — see the note above. Kept as a
 * named constant rather than inlined so it is obvious what this is and nobody
 * mistakes it for a secret that could be rotated to any effect.
 */
const PUBLIC_KEY_MATERIAL = 'wealthmax.crm.remember-me.v2';
const PUBLIC_KEY_SALT = 'wealthmax.crm.remember-me.salt.v2';
const PBKDF2_ITERATIONS = 100_000;
const IV_BYTES = 12;

/** Web Crypto is unavailable outside a secure context (plain-http on a LAN IP). */
function getSubtle(): SubtleCrypto | null {
  if (typeof globalThis === 'undefined') return null;
  return globalThis.crypto?.subtle ?? null;
}

let cachedKey: Promise<CryptoKey> | null = null;

function getKey(subtle: SubtleCrypto): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  const encoder = new TextEncoder();
  cachedKey = subtle
    .importKey('raw', encoder.encode(PUBLIC_KEY_MATERIAL), 'PBKDF2', false, ['deriveKey'])
    .then((baseKey) =>
      subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: encoder.encode(PUBLIC_KEY_SALT),
          iterations: PBKDF2_ITERATIONS,
          hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
      ),
    )
    .catch((error) => {
      // Don't cache a rejected promise — a transient failure would otherwise
      // poison every later read and write for the lifetime of the page.
      cachedKey = null;
      throw error;
    });

  return cachedKey;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

// Pinned to `ArrayBuffer` rather than the default `ArrayBufferLike`: Web Crypto's
// BufferSource excludes SharedArrayBuffer, so the looser type is rejected.
function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function encrypt(payload: RememberedCredentials): Promise<string | null> {
  const subtle = getSubtle();
  if (!subtle) return null;

  try {
    const key = await getKey(subtle);
    // A fresh IV per write. Reusing one across writes under the same key is the
    // one mistake that would break AES-GCM outright rather than merely leaving
    // it reversible-by-design.
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const ciphertext = await subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(JSON.stringify(payload)),
    );

    const packed = new Uint8Array(iv.length + ciphertext.byteLength);
    packed.set(iv, 0);
    packed.set(new Uint8Array(ciphertext), iv.length);

    return ENCRYPTED_PREFIX + toBase64(packed);
  } catch {
    return null;
  }
}

async function decrypt(stored: string): Promise<RememberedCredentials | null> {
  const subtle = getSubtle();
  if (!subtle) return null;

  try {
    const packed = fromBase64(stored.slice(ENCRYPTED_PREFIX.length));
    if (packed.length <= IV_BYTES) return null;

    const key = await getKey(subtle);
    const plaintext = await subtle.decrypt(
      { name: 'AES-GCM', iv: packed.subarray(0, IV_BYTES) },
      key,
      packed.subarray(IV_BYTES),
    );

    const parsed = JSON.parse(new TextDecoder().decode(plaintext)) as RememberedCredentials;
    return parsed?.email ? { email: parsed.email, password: parsed.password ?? '' } : null;
  } catch {
    // Tampered, truncated, or written by a build with different key material.
    return null;
  }
}

function readRaw(key: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    // Storage throws outright in some private/blocked-cookie modes.
    return '';
  }
}

function writeRaw(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Non-fatal: remembering the login is a convenience, not a requirement.
  }
}

/**
 * Returns the remembered credentials, or null when nothing is remembered.
 *
 * A legacy plaintext entry is re-encrypted in place as a side effect, so the
 * plaintext stops being readable from the moment this first runs.
 */
export async function readRememberedCredentials(key: string): Promise<RememberedCredentials | null> {
  const stored = readRaw(key);
  if (!stored) return null;

  if (stored.startsWith(ENCRYPTED_PREFIX)) {
    const credentials = await decrypt(stored);
    // Undecryptable entries are dead weight; clear so the user starts clean.
    if (!credentials) writeRaw(key, '');
    return credentials;
  }

  // Legacy plaintext `{ email, password }` from builds before 2026-08-27.
  if (stored.startsWith('{')) {
    let legacy: RememberedCredentials | null = null;
    try {
      const parsed = JSON.parse(stored) as Partial<RememberedCredentials>;
      if (parsed?.email) legacy = { email: parsed.email, password: parsed.password ?? '' };
    } catch {
      // Malformed — fall through and clear.
    }

    if (!legacy) {
      writeRaw(key, '');
      return null;
    }

    // Re-encrypt immediately. If Web Crypto is unavailable we must NOT leave
    // the plaintext in place, so the password is dropped and only the email
    // is kept — degraded convenience beats a readable password.
    const encrypted = await encrypt(legacy);
    writeRaw(key, encrypted ?? legacy.email);
    return encrypted ? legacy : { email: legacy.email, password: '' };
  }

  // Bare email string: remembered, but with no password stored.
  return { email: stored, password: '' };
}

/**
 * Stores the credentials when "remember me" is ticked, clears the entry when
 * it is not. Falls back to storing the email alone if Web Crypto is
 * unavailable — the password is never written in the clear.
 */
export async function persistRememberedCredentials(
  key: string,
  email: string,
  password: string,
  rememberMe: boolean,
): Promise<void> {
  if (!rememberMe || !email) {
    writeRaw(key, '');
    return;
  }

  const encrypted = await encrypt({ email, password });
  writeRaw(key, encrypted ?? email);
}

/**
 * Bootstrap hook: upgrades any entry left by an older build, so a plaintext
 * password stops being readable on the user's very next page load rather than
 * whenever they next happen to visit /login. Reading performs the migration,
 * so the resolved value is deliberately discarded.
 */
export async function migrateLegacyRememberMe(key: string): Promise<void> {
  await readRememberedCredentials(key);
}
